/**
 * Rentvine Prompt Templates
 * Templates for extracting fields from conversations for Rentvine push integrations
 */
import { z } from 'zod';
// ============================================================================
// Work Order
// ============================================================================
/**
 * Schema for Rentvine work order field extraction response
 *
 * LLM extracts these fields from the conversation:
 * - priority: Low, Medium, or High based on urgency
 * - vendorInstructions: Special instructions for vendor (not already in summary)
 *
 * Fields NOT extracted by LLM (set by push service):
 * - propertyID: From propertyExternalId
 * - workOrderStatusID: From metadata.defaultWorkOrderStatusID
 * - isOwnerApproved, isVacant, isSharedWith*: Defaults
 * - description: From jobData.summary + rawText
 * - sourceTypeID: Mapped from channel (SMS/EMAIL/CALL)
 */
export const RentvineWorkOrderSchema = z.object({
    priority: z
        .enum(['Low', 'Medium', 'High'])
        .describe('Work order priority level'),
    vendorInstructions: z
        .string()
        .nullable()
        .describe('Special instructions for the vendor (null if none)'),
    reasoning: z.string().describe('Brief explanation of the field selections'),
});
/**
 * Prompt template for extracting Rentvine work order fields
 * Used to extract priority and vendor instructions from conversations
 *
 * Simpler than Buildium - no category selection, entry access fields,
 * or contact info extraction needed
 */
export const rentvineWorkOrderTemplate = {
    name: 'rentvine-work-order',
    version: '1.0.0',
    systemPrompt: `You are a property management assistant that extracts work order information from maintenance conversations.

Your task is to carefully analyze the conversation and extract priority level and any special vendor instructions.

PRIORITY GUIDELINES:
- "High": ONLY for true emergencies that pose immediate risk:
  * Active water leaks causing damage
  * No heat when outside temperature is below freezing
  * Gas smell or suspected gas leak
  * Electrical hazards (sparking, burning smell)
  * Security issues (broken locks, broken windows)
  * Sewage backup
  * No hot water in winter
- "Medium": Most maintenance issues (DEFAULT - use this for typical repairs)
  * Appliances not working properly
  * Minor plumbing issues (slow drains, dripping faucets)
  * HVAC issues that aren't emergencies
  * Cosmetic damage
- "Low": Non-urgent, quality-of-life improvements
  * Cosmetic requests
  * Minor inconveniences
  * Scheduled maintenance

VENDOR INSTRUCTIONS - ONLY include information NOT already in the issue summary:
- DO include (if mentioned and not in summary):
  * How long the issue has been happening
  * What the resident has already tried to fix it
  * Specific location within the unit (e.g., "master bathroom", "kitchen near sink")
  * Any relevant history (e.g., "this happened before last month")
  * Entry access information (codes, key locations, pets)
  * Best times to schedule
- Set to null if there's nothing useful to add beyond what's in the summary
- DO NOT repeat: the main issue description, contact information, phone numbers`,
    userPrompt: `Analyze this maintenance conversation and extract work order fields:

ISSUE SUMMARY:
{{summary}}

FULL CONVERSATION TRANSCRIPT:
{{transcript}}

Instructions:
1. Read the ENTIRE transcript carefully - important details are often mentioned later in the conversation
2. Only set priority to "High" for true emergencies
3. vendorInstructions should ONLY contain additional context NOT already in the summary - set to null if nothing new to add`,
    variables: ['summary', 'transcript'],
    metadata: {
        maxTokens: 500,
        responseFormat: 'json_object',
    },
};
//# sourceMappingURL=rentvine.js.map